/**
 * PARÂMETROS DO PDVoz — o que a tela PDVoz edita e o que cada aparelho recebe.
 *
 * Antes, só as chaves de IA moravam no servidor; o resto ficava nas Configurações de
 * CADA aparelho (tablet do totem, monitor da cozinha, maquininha do garçom). Agora os
 * parâmetros que são da LOJA ficam aqui e chegam em todo aparelho pelo mesmo caminho
 * das chaves: GET /rest/pdvoz/config/{id} (sistemaadm, PdvozResource), no login, ao
 * abrir o app e a cada 30 min. Nas Configurações do aparelho sobrou só o que é dele
 * (modo da tela, Stone, microfone, fonte, tela invertida…).
 *
 * ⚠ CONTRATO EM TRÊS REPOSITÓRIOS. A lista de chaves, os tipos e os padrões espelham
 * `js/logica/config-loja.js` e os DEFAULTS de `js/db.js` do PDVoz (pwavoz). O backend
 * guarda o JSON sem interpretar. O PDVoz só aplica o que está na lista dele e com o
 * tipo certo — valor fora do esperado é ignorado lá em silêncio, então a validação
 * daqui (validarParametros) é o que avisa o lojista.
 */

/** Campos que o SERVIDOR injeta no GET e descarta no POST (moram no ParametroGeral). */
export const CAMPOS_INJETADOS = ["fonte", "stoneCode", "pinGerente", "senhaEstorno"]

export const CHAVES_DE_IA = ["openaiApiKey", "anthropicApiKey", "elevenlabsApiKey"]

// ── Palavras difíceis ───────────────────────────────────────────────────────
// Banco da LOJA (correção da fala transcrita), na chave palavrasDificeis do mesmo JSON.
// Só muda por POST /rest/pdvoz/palavras/{id} (PdvozPalavras no sistemaadm). O PDVoz treina
// no aparelho (microfone) e manda pra lá; aqui se vê, inclui na mão e apaga.

export const CHAVE_PALAVRAS = "palavrasDificeis"

export type PalavraDificil = {
    chave: string
    termo: string
    variantes: string[]
    deTreino?: string[]
    origem?: string
    treinos?: number
    criadoEm?: number
}

/** A MESMA normalização do PDVoz (logica/palavras.js → normalizar): é ela que dá a `chave`. */
export function normalizarPalavra(s: string): string {
    return String(s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
}

/** Entrada nova, no formato do PDVoz (novaPalavra lá). Variantes vêm separadas por vírgula. */
export function novaPalavraDificil(termo: string, variantesCsv: string): PalavraDificil {
    const t = String(termo || "").trim()
    const chave = normalizarPalavra(t)
    if (!chave) throw new Error("Informe a palavra")
    const vistos = new Set<string>()
    const variantes: string[] = []
    for (const v of String(variantesCsv || "").split(",")) {
        const n = normalizarPalavra(v)
        if (!n || n === chave || vistos.has(n)) continue
        vistos.add(n)
        variantes.push(n)
    }
    return { chave, termo: t, variantes, deTreino: [], origem: "manual", treinos: 0, criadoEm: Date.now() }
}

export type Tipo = "text" | "password" | "number" | "bool" | "select" | "textarea" | "color" | "categorias" | "palavra"

export type Opcao = { valor: string; rotulo: string }

export type Campo = {
    key: string
    label: string
    tipo: Tipo
    secao: string
    def: any
    opcoes?: Opcao[]
    hint?: string
    min?: number
    max?: number
    step?: number
    inteiro?: boolean
    /** regex que o valor (texto) precisa casar — a MESMA do PDVoz */
    padrao?: RegExp
    maxLen?: number
    /** cor vazia é aceita (= cor de fábrica)? */
    corVazia?: boolean
    /** mostrar só quando a condição valer (com os valores atuais da tela) */
    visivel?: (v: Record<string, any>) => boolean
}

export const SECOES = [
    "Chaves de IA",
    "Pedido e mesa",
    "Maquininha",
    "Cozinha — voz",
    "Cozinha — narração por IA",
    "Cozinha — categorias faladas",
    "Mãos livres (palavra de ativação)",
    "Transcrição e fala",
    "IA do pedido",
    "Totem",
    "Delivery",
    "Pagamento e WhatsApp",
    "Cores do estabelecimento",
]

const TTS_NATURAL = "gpt-4o-mini-tts"
const VOZES_NATURAIS = ["coral", "sage", "ash", "ballad", "verse"]
const VOZES_CLASSICAS = ["alloy", "echo", "fable", "onyx", "nova", "shimmer"]

/** Palavras .ppn hospedadas no PDVoz (models/) — a mesma lista de screens/config.js de lá. */
export const PALAVRAS_PPN = [{ id: "ei-chefe", label: "Ei Chefe", path: "./models/ei-chefe.ppn" }]

/** Palavras embutidas do Picovoice (enum BuiltInKeyword do SDK) — logica/wake.js do PDVoz. */
export const PALAVRAS_EMBUTIDAS = [
    "Alexa", "Americano", "Blueberry", "Bumblebee", "Computer", "Grapefruit",
    "Grasshopper", "HeyGoogle", "HeySiri", "Jarvis", "OkayGoogle",
    "Picovoice", "Porcupine", "Terminator",
]

const rotuloEmbutida = (nome: string) => String(nome || "").replace(/([a-z])([A-Z])/g, "$1 $2")

/** Modelos do openWakeWord que o PDVoz traz prontos — logica/wake-oww.js de lá. */
const PALAVRAS_OWW: Opcao[] = [
    { valor: "hey_jarvis", rotulo: "Hey Jarvis" },
    { valor: "alexa", rotulo: "Alexa" },
    { valor: "hey_mycroft", rotulo: "Hey Mycroft" },
    { valor: "hey_rhasspy", rotulo: "Hey Rhasspy" },
    { valor: "timer", rotulo: "Timer (palavra solta — teste)" },
    { valor: "weather", rotulo: "Weather (palavra solta — teste)" },
    { valor: "james", rotulo: "James (comunidade — recall baixo)" },
    { valor: "personalizada", rotulo: "Voz personalizada (cada aparelho grava a sua)" },
    { valor: "custom", rotulo: "Outro modelo .onnx…" },
]

const MODELOS_TRANSCRICAO: Opcao[] = [
    { valor: "whisper-1", rotulo: "OpenAI whisper-1 — o clássico (~US$ 0,006/min)" },
    { valor: "gpt-4o-mini-transcribe", rotulo: "OpenAI gpt-4o-mini-transcribe (~US$ 0,003/min)" },
    { valor: "gpt-4o-transcribe", rotulo: "OpenAI gpt-4o-transcribe — o mais preciso (~US$ 0,006/min)" },
    { valor: "scribe_v1", rotulo: "ElevenLabs Scribe v1 (créditos do plano)" },
    { valor: "scribe_v1_experimental", rotulo: "ElevenLabs Scribe v1 experimental (créditos do plano)" },
]

const MODELO_CLAUDE = /^claude-[a-z0-9][a-z0-9.-]*$/
const SO_DIGITOS = /^\d*$/
const NOME_TEMPLATE = /^[a-z0-9_]+$/

const naturalLigado = (v: Record<string, any>) => (v.producaoTtsModelo || TTS_NATURAL) === TTS_NATURAL
const picovoice = (v: Record<string, any>) => (v.wakeMotor || "picovoice") === "picovoice"

export const CAMPOS: Campo[] = [
    // ── Chaves de IA ────────────────────────────────────────────────────────
    { key: "openaiApiKey", label: "Chave OpenAI (voz e áudio)", tipo: "password", secao: "Chaves de IA", def: "", hint: "Transcreve a fala (Whisper) e fala os pedidos na cozinha (TTS)." },
    { key: "anthropicApiKey", label: "Chave Anthropic (interpretação)", tipo: "password", secao: "Chaves de IA", def: "", hint: "Interpreta o pedido falado e monta os itens do cardápio (Claude)." },
    { key: "elevenlabsApiKey", label: "Chave ElevenLabs (opcional)", tipo: "password", secao: "Chaves de IA", def: "", hint: "Só quando o modelo de transcrição é um Scribe." },

    // ── Pedido e mesa ───────────────────────────────────────────────────────
    { key: "pedidoVozTelefonePadrao", label: "Telefone padrão do pedido", tipo: "text", secao: "Pedido e mesa", def: "31999999999", padrao: SO_DIGITOS, maxLen: 13, hint: "Usado quando o cliente não informa telefone (mesa/retirada). A API exige um contato. Só números." },
    { key: "fechamentoFormaPadrao", label: "Forma de pagamento padrão do fechamento", tipo: "text", secao: "Pedido e mesa", def: "Dinheiro", maxLen: 60, hint: "Assumida ao fechar a mesa quando ninguém falar da forma de pagamento. Basta um pedaço do nome (\"pix\" acha \"PIX (online)\"). Vazio = sempre perguntar." },
    { key: "cobrancaWhatsAppMesa", label: "Cobrança por WhatsApp na mesa", tipo: "bool", secao: "Pedido e mesa", def: false, hint: "Mostra \"WhatsApp (cobrança)\" na folha da mesa do garçom. Desligado, a folha oferece só PIX, Crédito, Débito e Dinheiro." },
    {
        key: "opcoesUpsell", label: "Oferecer adicionais e opcionais (upsell)", tipo: "select", secao: "Pedido e mesa", def: "sempre",
        opcoes: [
            { valor: "sempre", rotulo: "Sempre — inclusive na tela do Garçom (padrão)" },
            { valor: "nunca", rotulo: "Nunca — só o obrigatório" },
        ],
        hint: "Grupo obrigatório (\"escolha 1\") é sempre perguntado. Aqui se decide só sobre os opcionais (molho extra, bacon a mais…). Com \"nunca\", o adicional continua no cardápio, escolhido na mão.",
    },
    { key: "pedidoVozFormaPagamentoPadrao", label: "Forma de pagamento no cadastro do pedido por voz", tipo: "text", secao: "Pedido e mesa", def: "", maxLen: 60, hint: "Vai no cadastro do pedido lançado por voz quando ninguém falou da forma. Vazio = Dinheiro. Não use forma online: o cadastro recusa." },
    { key: "pedidoVozValoresZerados", label: "Lançar o pedido por voz com valores zerados", tipo: "bool", secao: "Pedido e mesa", def: false, hint: "Os itens entram com preço zero (a cobrança acontece em outro sistema)." },
    { key: "mostrarBotaoTotem", label: "Mostrar o botão \"Totem\" na tela inicial", tipo: "bool", secao: "Pedido e mesa", def: false, hint: "Desmarcado não impede abrir o totem pelo \"Modo desta tela\" do aparelho." },
    { key: "mostrarBotaoDelivery", label: "Mostrar o botão \"Delivery\" na tela inicial", tipo: "bool", secao: "Pedido e mesa", def: false, hint: "Desmarcado não impede o cliente abrir o delivery pelo link do WhatsApp." },

    // ── Maquininha ──────────────────────────────────────────────────────────
    { key: "receberPedidosNaMaquininha", label: "Receber pedidos na maquininha", tipo: "bool", secao: "Maquininha", def: false, hint: "Liga na maquininha o Recebedor de Pedidos (o kanban deste painel): o botão 📋 no cabeçalho do garçom e, em cada aparelho, o modo Manual ou Automático, que toca, aceita e imprime a comanda. Desmarcado, nada de recebimento aparece na maquininha." },
    { key: "ocultarMicMaquininha", label: "Ocultar o microfone do pedido na maquininha", tipo: "bool", secao: "Maquininha", def: true, hint: "Esconde o botão de áudio nas telas de pedido do app da maquininha Stone — o atendimento fica só no toque. Ligado por padrão: o microfone interno da maquininha costuma gravar silêncio. Desmarque pra usar o pedido por voz (com fone Bluetooth). Não muda o PDVoz no navegador." },
    { key: "intervaloConsultaSeg", label: "Conferir com o servidor a cada (segundos)", tipo: "number", secao: "Maquininha", def: 4, min: 2, max: 10, step: 1, inteiro: true, hint: "Um tempo só pra todas as conferências curtas: a maquininha procurando cobrança nova no modo recebimento, o PIX caindo (totem e fechamento da conta), o totem esperando a maquininha remota e o pedido entrando na mesa. Menor responde mais rápido e pesa mais na rede e no servidor. No máximo 10: o servidor dá a maquininha do modo recebimento como desligada depois de 15 s sem notícia." },

    /* ── Os que ERAM da tela de Configurações do aparelho (PDVoz 1.94) ──────────────
       Até a 1.93 o operador digitava estes na própria maquininha. O dono decidiu em
       04/10 que tudo vem do servidor: lá a tela virou só leitura, com os campos
       travados e sem "Salvar". Enquanto não existirem AQUI, cada aparelho fica
       congelado no valor que já tinha — não há mais onde mudá-lo.
       ⚠ Valem por LOJA, não por maquininha (decisão do dono). Consequência a saber:
       `telaInvertida` e `avancoPapelStone` são traços FÍSICOS de cada aparelho, e
       passam a ser iguais em todos os da loja. */
    { key: "falarEmVozAlta", label: "A maquininha fala em voz alta", tipo: "bool", secao: "Maquininha", def: false, hint: "Anuncia o desfecho do pagamento e o retorno dos comandos de voz. Nasce DESLIGADO: num balcão, quem está na fila também ouve quanto o cliente da frente gastou." },
    { key: "cobrarCartaoStone", label: "Cobrar cartão pela maquininha (SDK da Stone)", tipo: "bool", secao: "Maquininha", def: false, hint: "Liga a cobrança rodando DENTRO da maquininha. Sem isto a folha não oferece o cartão no terminal." },
    { key: "telaInvertida", label: "Girar a tela 180°", tipo: "bool", secao: "Maquininha", def: false, hint: "Para a maquininha montada de cabeça para baixo no balcão. ⚠ Vale para TODAS as maquininhas da loja." },
    { key: "telaCheiaMaquininha", label: "Tela cheia (esconder a barra do Android)", tipo: "bool", secao: "Maquininha", def: false, hint: "Desligado (padrão): esconde só a barra de cima (relógio) e MANTÉM o botão Voltar do Android embaixo. Ligado: esconde as duas, e a maquininha fica 100% em tela cheia. ⚠ Sem o Voltar do sistema, o único caminho de volta é o do próprio app — as barras ainda reaparecem num deslize da borda." },
    { key: "imprimirComprovanteStone", label: "Imprimir comprovante do pagamento", tipo: "bool", secao: "Maquininha", def: true, hint: "A via do cartão/PIX depois de aprovado." },
    { key: "imprimirPedidoRecebimento", label: "Imprimir cupom do pedido", tipo: "bool", secao: "Maquininha", def: true, hint: "Os itens e a senha, para a cozinha ou para o cliente." },
    { key: "avancoPapelStone", label: "Avanço do papel ao fim do cupom (linhas)", tipo: "number", secao: "Maquininha", def: 6, min: 0, max: 30, inteiro: true, hint: "Quanto papel a impressora avança para o cupom poder ser destacado. Bobina não é de graça: 30 é o teto." },
    { key: "producaoFonteEscala", label: "Tamanho do texto na maquininha", tipo: "number", secao: "Maquininha", def: 1.15, min: 1, max: 2, step: 0.05, hint: "Multiplica o texto do app inteiro. 1 = normal." },
    { key: "vozRuidoPiso", label: "Piso de ruído do microfone", tipo: "number", secao: "Maquininha", def: 0, min: 0, max: 1, step: 0.01, hint: "Abaixo disto o app considera silêncio. 0 = o aparelho mede sozinho, que é o recomendado — este valor depende do salão e do fone." },
    { key: "stoneQrProviderId", label: "Chave de QR do PIX — provider id", tipo: "texto", secao: "Maquininha", def: "", hint: "⚠ A SDK da Stone NÃO lê estas chaves no SmartPOS (conferido no bytecode: o QR sai do gateway com authorization nula). Ficam aqui porque saíram da tela do aparelho; se confirmado que não servem, o certo é removê-las." },
    { key: "stoneQrAuthorization", label: "Chave de QR do PIX — authorization", tipo: "texto", secao: "Maquininha", def: "", hint: "Ver a observação do provider id." },

    // ── Cozinha — voz ───────────────────────────────────────────────────────
    {
        key: "producaoRepetirSeg", label: "Repetir pedido até preparar", tipo: "select", secao: "Cozinha — voz", def: 0, inteiro: true, min: 0, max: 600,
        opcoes: [{ valor: "0", rotulo: "Desligado — anuncia uma vez" }, ...[30, 60, 90, 120].map(n => ({ valor: String(n), rotulo: `A cada ${n}s` }))],
        hint: "Repete o anúncio de um pedido aceito até a cozinha marcar \"em produção\".",
    },
    {
        key: "producaoTempoPreparoMin", label: "Perguntar \"ficou pronto?\" após (em produção)", tipo: "select", secao: "Cozinha — voz", def: 0, inteiro: true, min: 0, max: 240,
        opcoes: [{ valor: "0", rotulo: "Desligado" }, ...[10, 15, 20, 30, 45].map(n => ({ valor: String(n), rotulo: `${n} min` }))],
        hint: "Passado esse tempo em produção, o sistema pergunta por voz se o pedido ficou pronto e já abre a escuta.",
    },
    { key: "producaoDitadoItemAItem", label: "Falar um item por vez e esperar a confirmação", tipo: "bool", secao: "Cozinha — voz", def: true, hint: "Desligado, a cozinha anuncia o pedido inteiro de uma vez." },
    { key: "producaoIntervaloSeg", label: "Buscar pedidos novos a cada (segundos)", tipo: "number", secao: "Cozinha — voz", def: 15, min: 5, max: 120, step: 1, inteiro: true, hint: "Intervalo com que o monitor da cozinha consulta os pedidos." },
    { key: "producaoAvisarPronto", label: "Avisar o garçom por voz quando o pedido entrar em entrega", tipo: "bool", secao: "Cozinha — voz", def: true, hint: "Fala a senha (e a mesa) quando a cozinha marca \"Em entrega\". Usa a chave OpenAI." },
    {
        key: "producaoTtsModelo", label: "Qualidade da voz", tipo: "select", secao: "Cozinha — voz", def: TTS_NATURAL,
        opcoes: [
            { valor: TTS_NATURAL, rotulo: "Natural — conversacional, aceita instruções de tom (recomendada)" },
            { valor: "tts-1", rotulo: "Padrão — rápida e barata" },
            { valor: "tts-1-hd", rotulo: "HD — melhor que a padrão, mais cara" },
        ],
        hint: "As vozes \"naturais\" e as instruções de tom só valem no modelo Natural; nos clássicos vale a velocidade.",
    },
    {
        key: "producaoTtsVoz", label: "Voz", tipo: "select", secao: "Cozinha — voz", def: "coral",
        opcoes: [...VOZES_NATURAIS.map(v => ({ valor: v, rotulo: `${v} (Natural)` })), ...VOZES_CLASSICAS.map(v => ({ valor: v, rotulo: v }))],
        hint: "coral é a recomendada em português. As marcadas \"(Natural)\" só funcionam no modelo Natural.",
    },
    { key: "producaoTtsVelocidade", label: "Velocidade da fala", tipo: "number", secao: "Cozinha — voz", def: 1.0, min: 0.5, max: 2, step: 0.05, visivel: v => !naturalLigado(v), hint: "1 é o normal (de 0,5 a 2). Só nos modelos clássicos." },
    { key: "producaoTtsInstrucoes", label: "Instruções de tom (modelo Natural)", tipo: "textarea", secao: "Cozinha — voz", def: "Fale em português do Brasil, em tom natural e conversacional, agradável e claro, com pequenas pausas entre as frases.", maxLen: 1000, visivel: naturalLigado, hint: "Idioma, tom e ritmo em linguagem natural." },

    // ── Cozinha — narração por IA ───────────────────────────────────────────
    { key: "producaoNarracaoIA", label: "Montar a fala do pedido com IA", tipo: "bool", secao: "Cozinha — narração por IA", def: false, hint: "Ligado: a IA escreve a fala seguindo as instruções (ex.: juntar sucos iguais). Usa a chave Anthropic." },
    {
        key: "producaoNarracaoModelo", label: "Modelo da IA", tipo: "select", secao: "Cozinha — narração por IA", def: "claude-haiku-4-5-20251001", padrao: MODELO_CLAUDE,
        opcoes: [
            { valor: "claude-haiku-4-5-20251001", rotulo: "Haiku — rápido e barato (recomendado)" },
            { valor: "claude-sonnet-4-6", rotulo: "Sonnet — mais capaz" },
        ],
        visivel: v => !!v.producaoNarracaoIA,
    },
    { key: "producaoNarracaoInstrucoes", label: "Instruções de narração", tipo: "textarea", secao: "Cozinha — narração por IA", def: "", maxLen: 4000, visivel: v => !!v.producaoNarracaoIA, hint: "Ex.: se saírem dois sucos do mesmo sabor, junte numa fala só. Fale sempre a mesa primeiro." },

    // ── Cozinha — categorias faladas ────────────────────────────────────────
    { key: "producaoCategoriasFaladas", label: "Categorias anunciadas na cozinha", tipo: "categorias", secao: "Cozinha — categorias faladas", def: [], hint: "Itens de categorias não marcadas não são anunciados (ex.: só cozinha quente, sem bebidas)." },

    // ── Mãos livres ─────────────────────────────────────────────────────────
    {
        key: "wakeMotor", label: "Motor", tipo: "select", secao: "Mãos livres (palavra de ativação)", def: "picovoice",
        opcoes: [
            { valor: "picovoice", rotulo: "Picovoice — \"Ei Chefe\" em português (precisa de AccessKey grátis)" },
            { valor: "openwakeword", rotulo: "openWakeWord — grátis, sem AccessKey (só palavras em inglês)" },
        ],
        hint: "Ligar a palavra de ativação continua sendo em cada aparelho (Configurações do PDVoz): é função de microfone. Aqui se escolhe qual palavra e como.",
    },
    { key: "wakeAccessKey", label: "AccessKey Picovoice", tipo: "password", secao: "Mãos livres (palavra de ativação)", def: "", maxLen: 500, visivel: picovoice, hint: "Gratuita em console.picovoice.ai." },
    { key: "wakeKeywordPath", label: "Palavra de ativação", tipo: "palavra", secao: "Mãos livres (palavra de ativação)", def: "./models/ei-chefe.ppn", maxLen: 300, visivel: picovoice, hint: "\"Ei Chefe\" já vem pronta e em português — é a recomendada." },
    { key: "wakeLabel", label: "Rótulo da palavra (exibição)", tipo: "text", secao: "Mãos livres (palavra de ativação)", def: "Ei Chefe", maxLen: 60, visivel: v => picovoice(v) && selecaoWake(v) === "custom" },
    { key: "wakeBuiltin", label: "Palavra embutida", tipo: "text", secao: "Mãos livres (palavra de ativação)", def: "Bumblebee", padrao: /^[A-Za-z_]+$/, maxLen: 40, visivel: () => false },
    { key: "wakeModeloPath", label: "Arquivo de idioma .pv (URL/caminho) — opcional", tipo: "text", secao: "Mãos livres (palavra de ativação)", def: "", maxLen: 300, visivel: v => picovoice(v) && selecaoWake(v) === "custom", hint: "Vazio = o app resolve sozinho." },
    { key: "wakeOwwPalavra", label: "Palavra de ativação (inglês)", tipo: "select", secao: "Mãos livres (palavra de ativação)", def: "hey_jarvis", padrao: /^[a-z0-9_]+$/, maxLen: 40, opcoes: PALAVRAS_OWW, visivel: v => !picovoice(v) },
    { key: "wakeOwwUrl", label: "Arquivo .onnx (URL/caminho)", tipo: "text", secao: "Mãos livres (palavra de ativação)", def: "", maxLen: 300, visivel: v => !picovoice(v) && v.wakeOwwPalavra === "custom", hint: "Só o nome do arquivo → o app procura em models/openwakeword/." },
    { key: "wakeOwwLabel", label: "Rótulo (exibição)", tipo: "text", secao: "Mãos livres (palavra de ativação)", def: "", maxLen: 60, visivel: v => !picovoice(v) && v.wakeOwwPalavra === "custom" },
    { key: "wakeSensibilidade", label: "Sensibilidade", tipo: "number", secao: "Mãos livres (palavra de ativação)", def: 0.6, min: 0.1, max: 0.9, step: 0.05, hint: "De 0,1 a 0,9. Mais alto pega mais fácil, mas dispara sozinho com mais frequência." },
    {
        key: "wakeJanelaSeg", label: "Ouvir o comando por", tipo: "select", secao: "Mãos livres (palavra de ativação)", def: 4, inteiro: true, min: 2, max: 60,
        opcoes: [3, 4, 5, 6, 8, 10, 15, 20, 30].map(n => ({ valor: String(n), rotulo: `${n} segundos` })),
    },
    { key: "wakeAutoAplicar", label: "Aplicar comando automaticamente (sem tocar na tela)", tipo: "bool", secao: "Mãos livres (palavra de ativação)", def: false, hint: "Aplica na hora um comando único e não-destrutivo, e fala o resultado." },

    // ── Transcrição e fala ──────────────────────────────────────────────────
    { key: "transcricaoModelo", label: "Modelo de transcrição", tipo: "select", secao: "Transcrição e fala", def: "scribe_v1", opcoes: MODELOS_TRANSCRICAO, hint: "O Scribe precisa da chave ElevenLabs; sem ela o app cai sozinho no whisper-1." },
    { key: "transcricaoVocabulario", label: "Mandar as palavras difíceis como dica de grafia", tipo: "bool", secao: "Transcrição e fala", def: true, hint: "Evita \"cream cheese\" virar \"creme de queijo\". O Scribe não aceita a dica." },
    { key: "falaLimpar", label: "Separar o pedido da conversa em volta", tipo: "bool", secao: "Transcrição e fala", def: true, hint: "Uma IA rápida retira da fala o que é conversa paralela antes de montar o pedido." },
    { key: "treinoLimiar", label: "Treino de palavras: semelhança mínima pra aceitar sozinho", tipo: "number", secao: "Transcrição e fala", def: 0.75, min: 0.5, max: 0.95, step: 0.05, hint: "De 0,5 a 0,95. Alto demais recusa justamente a transcrição torta, que é a que vale aprender." },
    { key: "vozAutoPausa", label: "Detectar sozinho quando a pessoa terminou de falar", tipo: "bool", secao: "Transcrição e fala", def: true, hint: "Desligado, a gravação só encerra num toque." },
    { key: "vozPausaSeg", label: "Pausa até entender que terminou (segundos)", tipo: "number", secao: "Transcrição e fala", def: 4, min: 0, max: 30, step: 0.5, visivel: v => v.vozAutoPausa !== false, hint: "Curto demais corta quem pausa pra pensar; longo demais faz todo pedido esperar no fim." },

    // ── IA do pedido ────────────────────────────────────────────────────────
    {
        key: "pedidoModelo", label: "Modelo de montagem do pedido", tipo: "select", secao: "IA do pedido", def: "claude-opus-4-7", padrao: MODELO_CLAUDE,
        opcoes: [
            { valor: "claude-sonnet-4-6", rotulo: "Sonnet 4.6 — mais barato" },
            { valor: "claude-sonnet-5", rotulo: "Sonnet 5 — mais capaz, custo Sonnet" },
            { valor: "claude-opus-4-7", rotulo: "Opus 4.7 — mais capaz, mais caro (padrão)" },
            { valor: "claude-opus-5", rotulo: "Opus 5 — o mais capaz" },
        ],
        hint: "Transforma a fala + cardápio no pedido montado. Mais capaz erra menos, mas custa mais por pedido.",
    },
    { key: "pedidoCardapioEnxuto", label: "Mandar só as categorias parecidas com o que foi falado", tipo: "bool", secao: "IA do pedido", def: false, hint: "Economiza tokens em cardápio grande, mas uma categoria cortada por engano vira \"item não encontrado\" e o cache do cardápio deixa de valer." },
    { key: "cardapioIaPreparar", label: "Preparar o texto do cardápio com IA", tipo: "bool", secao: "IA do pedido", def: true, hint: "Limpa ruído dos nomes e anota como se fala cada termo. Preço e id nunca passam pela IA." },

    // ── Totem ───────────────────────────────────────────────────────────────
    { key: "totemInatividadeSeg", label: "Voltar à tela de convite após (segundos sem toque)", tipo: "number", secao: "Totem", def: 120, min: 0, max: 3600, step: 1, inteiro: true, hint: "Zera o pedido de quem foi embora. 0 = nunca volta sozinho." },
    { key: "totemPedirSenha", label: "Pedir a senha da plaquinha (após o WhatsApp)", tipo: "bool", secao: "Totem", def: false, hint: "Para lojas com caixinha de plaquinhas numeradas: o número vai na observação do pedido." },
    { key: "totemFidelidadeWhats", label: "Destacar \"ganha pontos de fidelidade\" ao informar o WhatsApp", tipo: "bool", secao: "Totem", def: false, hint: "Ligue só se a loja tem programa de fidelidade." },
    { key: "totemVerTranscricao", label: "Botão 🎙 para ver a transcrição em cada item", tipo: "bool", secao: "Totem", def: true, hint: "Ferramenta de auditoria na tela do cliente — desligue quando estiver rodando." },
    { key: "totemOcultarEsgotados", label: "Não exibir produtos esgotados", tipo: "bool", secao: "Totem", def: false, hint: "Desligado, o esgotado aparece marcado como \"Esgotado\". Ligado, ele some do cardápio do totem (a categoria só com esgotados some junto)." },
    { key: "totemOcultarCobrancaWhats", label: "Não oferecer cobrança pelo WhatsApp", tipo: "bool", secao: "Totem", def: false, hint: "Com maquininha, some o botão \"Cobrança no WhatsApp\" (ficam Crédito, Débito e Pix). Sem maquininha, o totem vai direto para o QR do Pix na tela, sem pedir o WhatsApp." },
    { key: "totemPedirNome", label: "Pedir o nome do cliente", tipo: "bool", secao: "Totem", def: false, hint: "Depois do \"Para viagem / Consumo no local\", o totem pergunta o nome. Ele sai na comanda no lugar de \"Totem\"." },
    { key: "totemConversacional", label: "Totem conversacional (fala os itens e pergunta as opções)", tipo: "bool", secao: "Totem", def: false, hint: "Usa a voz do TTS (chave OpenAI)." },
    { key: "totemCardFundo", label: "Cor de fundo do card de produto", tipo: "color", secao: "Totem", def: "#ffffff", corVazia: false, hint: "O texto do card é calculado a partir dela." },

    // ── Delivery ────────────────────────────────────────────────────────────
    { key: "deliverySite", label: "Site de pedidos usado pelo delivery", tipo: "text", secao: "Delivery", def: "", maxLen: 200, hint: "Vazio é o normal: o app usa o site das cores ou descobre pelo estabelecimento do login." },
    { key: "deliveryTaxaAdicional", label: "Taxa fixa de serviço (R$)", tipo: "number", secao: "Delivery", def: 0, min: 0, max: 1000, step: 0.01, hint: "Somada a todo pedido de delivery. Só vale se a loja não tem o PUBLIC_TAXA_ADICIONAL cadastrado." },

    // ── Pagamento e WhatsApp ────────────────────────────────────────────────
    {
        key: "pagamentoWhatsModo", label: "Pagamento no WhatsApp (totem e garçom)", tipo: "select", secao: "Pagamento e WhatsApp", def: "ambos",
        opcoes: [
            { valor: "pix", rotulo: "Só PIX — copia e cola" },
            { valor: "link", rotulo: "Só link do cartão (Pagar.me)" },
            { valor: "ambos", rotulo: "Ambos — PIX e link do cartão (padrão)" },
        ],
    },
    { key: "pagamentoPixComoLink", label: "Enviar o PIX como link \"Copiar código\" (em vez do código cru)", tipo: "bool", secao: "Pagamento e WhatsApp", def: true, hint: "Evita o código virar \"link azul cortado\" no WhatsApp." },
    {
        key: "pagamentoPixCanal", label: "Canal do PIX no WhatsApp", tipo: "select", secao: "Pagamento e WhatsApp", def: "oficial",
        opcoes: [
            { valor: "central", rotulo: "Central — texto" },
            { valor: "oficial", rotulo: "API oficial — template com botão (padrão)" },
        ],
        hint: "No oficial, o PIX vai como template com botão \"Copiar código Pix\" pelo número oficial da Cliente Fiel. Exige os templates aprovados abaixo.",
    },
    { key: "whatsappOficialPhoneId", label: "phoneId do número oficial", tipo: "text", secao: "Pagamento e WhatsApp", def: "1026790147190491", padrao: SO_DIGITOS, maxLen: 30 },
    { key: "whatsappWabaId", label: "wabaId da conta Cliente Fiel", tipo: "text", secao: "Pagamento e WhatsApp", def: "34664941576482632", padrao: SO_DIGITOS, maxLen: 30, hint: "Usado pra criar e consultar os templates." },
    { key: "whatsappPixTemplate", label: "Template só-PIX (1 botão)", tipo: "text", secao: "Pagamento e WhatsApp", def: "pdvoz_pix2", padrao: NOME_TEMPLATE, maxLen: 512 },
    { key: "whatsappPixCartaoTemplate", label: "Template PIX + cartão (2 botões)", tipo: "text", secao: "Pagamento e WhatsApp", def: "pdvoz_pix_cartao2", padrao: NOME_TEMPLATE, maxLen: 512 },
    { key: "whatsappComprovanteTemplate", label: "Template do comprovante (sem botão)", tipo: "text", secao: "Pagamento e WhatsApp", def: "pdvoz_comprovante", padrao: NOME_TEMPLATE, maxLen: 512, hint: "Nome só com letras minúsculas, números e _. A Meta bloqueia reusar um nome apagado por ~4 semanas: pra mudar um template, troque o nome." },
    { key: "pixCobrancaPorValor", label: "Cobrança PIX por valor na conta dividida (proposta — ainda não vale)", tipo: "bool", secao: "Pagamento e WhatsApp", def: false, hint: "A API de hoje cobra o pedido inteiro: ligado, quem deve uma parte receberia o código do total. Deixe desligado." },

    // ── Cores ───────────────────────────────────────────────────────────────
    { key: "temaSite", label: "Site de pedidos do estabelecimento", tipo: "text", secao: "Cores do estabelecimento", def: "", maxLen: 200, hint: "De onde o app busca as cores (e as regras do delivery). Vazio = o app pergunta ao servidor pelo estabelecimento do login." },
    { key: "temaSiteAuto", label: "Manter as cores em dia sozinho (buscar do site ao abrir)", tipo: "bool", secao: "Cores do estabelecimento", def: true, hint: "Ligado e com o site preenchido, as cores vêm do site (o app as busca ao abrir) e as de baixo são ignoradas. Desligue para usar as de baixo." },
    { key: "temaCorPrimaria", label: "Cor primária", tipo: "color", secao: "Cores do estabelecimento", def: "#b30000", corVazia: true, hint: "Botões, abas e destaques (corSitePrimaria)." },
    { key: "temaCorSecundaria", label: "Cor secundária", tipo: "color", secao: "Cores do estabelecimento", def: "", corVazia: true, hint: "Cozinha e apoios (corSiteSecundaria). Vazio = de fábrica." },
    { key: "temaCorBarra", label: "Cor do botão de finalizar", tipo: "color", secao: "Cores do estabelecimento", def: "", corVazia: true, hint: "No totem pinta o botão de enviar (corSiteBarraFinalizar). Vazio = de fábrica." },
]

/** As chaves da LOJA — exatamente as de PARAMETROS_DA_LOJA no config-loja.js do PDVoz. */
export const CHAVES_DA_LOJA = CAMPOS.filter(c => !CHAVES_DE_IA.includes(c.key)).map(c => c.key)

export function defaultsDoSchema(): Record<string, any> {
    const o: Record<string, any> = {}
    for (const c of CAMPOS) o[c.key] = Array.isArray(c.def) ? [...c.def] : c.def
    return o
}

// ── Palavra de ativação (Picovoice) ─────────────────────────────────────────
// No PDVoz a palavra são QUATRO settings (wakeKeywordPath, wakeLabel, wakeBuiltin,
// wakeModeloPath) derivadas de UM seletor. Aqui é igual: o seletor diz qual, e
// aplicarPalavra grava o conjunto coerente — nunca meio-a-meio.

const normEmbutida = (v: string) => String(v || "").replace(/[_\s]/g, "").toLowerCase()

/** 'ppn:<id>' | 'builtin:<Nome>' | 'custom' — qual opção do seletor os valores representam. */
export function selecaoWake(v: Record<string, any>): string {
    const path = String(v.wakeKeywordPath || "").trim()
    if (path) {
        const hit = PALAVRAS_PPN.find(p => p.path === path)
        return hit ? `ppn:${hit.id}` : "custom"
    }
    const salvo = normEmbutida(v.wakeBuiltin) || "bumblebee"
    const b = PALAVRAS_EMBUTIDAS.find(w => normEmbutida(w) === salvo || (salvo === "okgoogle" && w === "OkayGoogle"))
    return `builtin:${b || "Bumblebee"}`
}

export const OPCOES_PALAVRA: Opcao[] = [
    ...PALAVRAS_PPN.map(p => ({ valor: `ppn:${p.id}`, rotulo: `${p.label} (português — recomendada)` })),
    ...PALAVRAS_EMBUTIDAS.map(w => ({ valor: `builtin:${w}`, rotulo: `${rotuloEmbutida(w)} (inglês, embutida)` })),
    { valor: "custom", rotulo: "Outro arquivo .ppn…" },
]

/** Os campos que mudam quando se escolhe uma opção do seletor de palavra. */
export function aplicarPalavra(v: Record<string, any>, sel: string): Record<string, any> {
    if (sel.startsWith("ppn:")) {
        const p = PALAVRAS_PPN.find(x => x.id === sel.slice(4)) || PALAVRAS_PPN[0]
        return { wakeKeywordPath: p.path, wakeLabel: p.label, wakeModeloPath: "" }
    }
    if (sel.startsWith("builtin:")) {
        const w = sel.slice("builtin:".length)
        return { wakeKeywordPath: "", wakeBuiltin: w, wakeLabel: rotuloEmbutida(w), wakeModeloPath: "" }
    }
    // custom: mantém o que houver, mas sem apontar pra uma palavra hospedada
    const atual = String(v.wakeKeywordPath || "")
    const hospedada = PALAVRAS_PPN.some(p => p.path === atual)
    return { wakeKeywordPath: hospedada || !atual ? "./models/minha-palavra.ppn" : atual }
}

// ── Validação (a MESMA do PDVoz) e montagem do que vai pro servidor ────────

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

function paraNumero(v: any): number {
    if (typeof v === "number") return v
    if (typeof v === "string" && v.trim()) return Number(v.trim().replace(",", "."))
    return NaN
}

/** Normaliza UM valor pro tipo do campo. { ok:false, erro } quando o PDVoz recusaria. */
export function normalizar(c: Campo, bruto: any): { ok: true; valor: any } | { ok: false; erro: string } {
    switch (c.tipo) {
        case "bool":
            return { ok: true, valor: !!bruto }
        case "number":
        case "select":
            if (c.inteiro !== undefined || c.min !== undefined || c.tipo === "number") {
                const n = paraNumero(bruto)
                if (!Number.isFinite(n)) return { ok: false, erro: "informe um número" }
                if ((c.min !== undefined && n < c.min) || (c.max !== undefined && n > c.max)) {
                    return { ok: false, erro: `use um valor entre ${c.min} e ${c.max}` }
                }
                return { ok: true, valor: c.inteiro ? Math.round(n) : n }
            }
            break
        case "color": {
            const v = String(bruto ?? "").trim()
            if (!v) return c.corVazia ? { ok: true, valor: "" } : { ok: false, erro: "escolha uma cor" }
            return HEX.test(v) ? { ok: true, valor: v.toLowerCase() } : { ok: false, erro: "cor no formato #rrggbb" }
        }
        case "categorias": {
            const lista = Array.isArray(bruto) ? bruto : []
            return { ok: true, valor: Array.from(new Set(lista.map((x: any) => String(x).trim()).filter(Boolean))) }
        }
        default:
            break
    }
    // texto (text, password, textarea, palavra, select de texto)
    const v = String(bruto ?? "").trim()
    if (c.maxLen !== undefined && v.length > c.maxLen) return { ok: false, erro: `no máximo ${c.maxLen} caracteres` }
    if (v && c.padrao && !c.padrao.test(v)) {
        if (c.padrao === SO_DIGITOS) return { ok: false, erro: "só números" }
        if (c.padrao === NOME_TEMPLATE) return { ok: false, erro: "só letras minúsculas, números e _" }
        return { ok: false, erro: "valor inválido" }
    }
    if (c.opcoes && c.tipo === "select" && v && !c.opcoes.some(o => o.valor === v) && !c.padrao) {
        return { ok: false, erro: "escolha uma das opções" }
    }
    return { ok: true, valor: v }
}

const visivelNaTela = (c: Campo, valores: Record<string, any>) => !c.visivel || c.visivel(valores)

/** Erros por campo (chave → mensagem). Vazio = pode salvar. Só campos VISÍVEIS: um erro
    num campo escondido travaria o salvar sem a pessoa ter onde corrigir (o valor torto de
    um campo escondido volta ao padrão em montarConfigParaSalvar). */
export function validarParametros(valores: Record<string, any>): Record<string, string> {
    const erros: Record<string, string> = {}
    for (const c of CAMPOS) {
        if (!visivelNaTela(c, valores)) continue
        const r = normalizar(c, valores[c.key])
        if (!r.ok) erros[c.key] = r.erro
    }
    return erros
}

/**
 * O corpo do POST /rest/pdvoz/config/{id}. O POST SUBSTITUI o JSON inteiro, então parte
 * do que o GET devolveu (preserva chaves que esta tela não conhece), tira o que o servidor
 * injeta e grava as chaves de IA e os parâmetros normalizados.
 */
export function montarConfigParaSalvar(original: Record<string, any>, valores: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = { ...(original || {}) }
    for (const k of CAMPOS_INJETADOS) delete out[k]
    // o banco de palavras tem endpoint próprio (o backend ignora o que vier aqui)
    delete out[CHAVE_PALAVRAS]
    for (const c of CAMPOS) {
        const r = normalizar(c, valores[c.key])
        // torto (só acontece em campo escondido — os visíveis passam por validarParametros
        // antes): volta ao padrão, em vez de deixar no JSON um valor que o PDVoz ignoraria
        out[c.key] = r.ok ? r.valor : (Array.isArray(c.def) ? [...c.def] : c.def)
    }
    return out
}
